import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/AppIcon";
import Button from "../components/ui/Button";
import { useAuth } from "../context/AuthContext";
import { listBusinessJobs, getBusinessOverview } from "../api/client";

// Jobs — the recruiter's list of openings. Each job runs either a single AI
// interview or a multi-round pipeline; this page is the entry point to create
// one and to drill into a job's rounds + candidate funnel. Writes (create) are
// gated to owner/admin/recruiter; viewers get a read-only list.

const WRITER_ROLES = ["owner", "admin", "recruiter"];

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "draft", label: "Drafts" },
  { key: "active", label: "Active" },
  { key: "closed", label: "Closed" },
];

const STATUS_BADGE = {
  draft: "bg-muted text-muted-foreground",
  active: "bg-success/15 text-success",
  closed: "bg-warning/15 text-warning",
  archived: "bg-muted text-muted-foreground",
};

function StatusBadge({ status }) {
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium capitalize " +
        (STATUS_BADGE[status] || "bg-muted text-muted-foreground")
      }
    >
      {status === "active" && <span className="h-1.5 w-1.5 rounded-full bg-success" />}
      {status || "draft"}
    </span>
  );
}

function ModeBadge({ mode, numRounds }) {
  const isPipeline = mode === "pipeline";
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
      <Icon name={isPipeline ? "GitBranch" : "MessageSquare"} size={12} />
      {isPipeline ? `${numRounds}-round pipeline` : "Single interview"}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function Jobs() {
  const navigate = useNavigate();
  const { organization } = useAuth();
  const canWrite = WRITER_ROLES.includes(organization?.role);

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // Map of jobId -> count of rounds paused for manual review, from the org overview.
  const [reviewByJob, setReviewByJob] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const data = await listBusinessJobs(params);
      setJobs(data.jobs || []);
    } catch (err) {
      setLoadError(err?.error || "Could not load jobs.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Overview is org-wide (not filtered), so load it once to badge jobs that have
  // a round paused for manual gate review. Best-effort — never blocks the list.
  useEffect(() => {
    let alive = true;
    (async () => {
      const ov = await getBusinessOverview();
      if (!alive || !ov || ov.error || !Array.isArray(ov.awaitingReview)) return;
      const map = {};
      for (const r of ov.awaitingReview) {
        if (!r?.jobId) continue;
        map[r.jobId] = (map[r.jobId] || 0) + 1;
      }
      setReviewByJob(map);
    })();
    return () => {
      alive = false;
    };
  }, []);

  const counts = useMemo(() => {
    // Only meaningful on the unfiltered view; used for the tab hints.
    const c = { "": jobs.length };
    return c;
  }, [jobs]);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Jobs</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Create an opening, define its interview rounds, and invite candidates.
          </p>
        </div>
        {canWrite && (
          <Button size="sm" iconName="Plus" onClick={() => navigate("/business/jobs/new")}>
            Create job
          </Button>
        )}
      </div>

      {/* Status filter */}
      <div className="flex flex-wrap items-center gap-1.5">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            onClick={() => setStatusFilter(f.key)}
            className={
              "rounded-full px-3 py-1.5 text-sm font-medium transition-colors " +
              (statusFilter === f.key
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground")
            }
          >
            {f.label}
            {f.key === "" && counts[""] ? (
              <span className="ml-1.5 opacity-70">{counts[""]}</span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Error */}
      {loadError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          <span>{loadError}</span>
          <button onClick={load} className="ml-auto font-medium underline">
            Retry
          </button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl bg-muted/50" />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card px-6 py-16 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Icon name="Briefcase" size={22} />
          </div>
          <h2 className="mt-4 text-base font-semibold text-foreground">
            {statusFilter ? "No jobs match this filter" : "No jobs yet"}
          </h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
            {statusFilter
              ? "Try a different status, or create a new job."
              : "Create your first opening to start interviewing candidates automatically."}
          </p>
          {canWrite && (
            <Button
              className="mt-5"
              size="sm"
              iconName="Plus"
              onClick={() => navigate("/business/jobs/new")}
            >
              Create job
            </Button>
          )}
        </div>
      ) : (
        <ul className="space-y-3">
          {jobs.map((job) => {
            const deadline = formatDate(job.deadlineAt);
            const reviewCount = reviewByJob[job.id] || 0;
            return (
              <li key={job.id}>
                <button
                  onClick={() => navigate(`/business/jobs/${job.id}`)}
                  className="flex w-full flex-col gap-3 rounded-xl border border-border bg-card p-5 text-left transition-colors hover:border-primary/40 hover:bg-muted/30 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate text-base font-semibold text-foreground">
                        {job.title}
                      </h3>
                      <StatusBadge status={job.status} />
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                      {job.department && <span>{job.department}</span>}
                      {job.department && job.location && <span className="opacity-40">·</span>}
                      {job.location && <span>{job.location}</span>}
                    </div>
                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      <ModeBadge mode={job.mode} numRounds={job.numRounds} />
                      {deadline && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                          <Icon name="CalendarClock" size={12} />
                          Due {deadline}
                        </span>
                      )}
                      {reviewCount > 0 && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-warning/15 px-2.5 py-0.5 text-xs font-medium text-warning">
                          <Icon name="AlertCircle" size={12} />
                          {reviewCount} awaiting review
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-6">
                    <div className="text-center">
                      <div className="text-lg font-semibold tabular-nums text-foreground">
                        {job.candidateCount ?? 0}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        candidate{(job.candidateCount ?? 0) === 1 ? "" : "s"}
                      </div>
                    </div>
                    <Icon name="ChevronRight" size={18} className="text-muted-foreground" />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
